import React, { useMemo, useState } from 'react';
import {
  Box,
  Chip,
  IconButton,
  LinearProgress,
  Paper,
  Typography,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

const tutorialVideos = [
  { id: '3cAIvFIMbsI', title: 'Clara', url: 'https://youtu.be/3cAIvFIMbsI' },
  { id: 'gJYPjYLxHEA', title: 'Dashboard', url: 'https://youtu.be/gJYPjYLxHEA' },
  { id: 'eGerIAG1H_o', title: 'Gerenciamento de Vagas', url: 'https://youtu.be/eGerIAG1H_o' },
  { id: '56t7_m_69IU', title: 'Gerenciamento de Etapas', url: 'https://youtu.be/56t7_m_69IU' },
  { id: 'HZBQNjwzwEA', title: 'Gerenciamento de Candidaturas', url: 'https://youtu.be/HZBQNjwzwEA' },
];

export default function Tutorial() {
  const [activeIndex, setActiveIndex] = useState(0);
  const activeVideo = tutorialVideos[activeIndex];

  const embedUrl = useMemo(() => (
    `https://www.youtube-nocookie.com/embed/${activeVideo.id}?rel=0&modestbranding=1`
  ), [activeVideo.id]);

  const progress = ((activeIndex + 1) / tutorialVideos.length) * 100;

  const handlePrevious = () => {
    setActiveIndex((current) => (
      current === 0 ? tutorialVideos.length - 1 : current - 1
    ));
  };

  const handleNext = () => {
    setActiveIndex((current) => (
      current === tutorialVideos.length - 1 ? 0 : current + 1
    ));
  };

  return (
    <Box sx={{ maxWidth: 1120, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Cabeçalho com identidade e progresso da série */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: '9px',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'primary.main',
            bgcolor: 'rgba(0, 51, 102, 0.08)',
          }}
        >
          <OndemandVideoIcon sx={{ fontSize: 20 }} />
        </Box>
        <Box sx={{ minWidth: 200, flex: 1 }}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
            Tutorial
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Aprenda a usar o Currículo Certo em {tutorialVideos.length} vídeos rápidos
          </Typography>
        </Box>
        <Box sx={{ width: { xs: '100%', sm: 220 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: '0.03em' }}>
              SEU PROGRESSO
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 800, color: 'primary.main' }}>
              {activeIndex + 1} / {tutorialVideos.length}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 5,
              borderRadius: 999,
              bgcolor: 'rgba(0, 51, 102, 0.10)',
              '& .MuiLinearProgress-bar': { borderRadius: 999 },
            }}
          />
        </Box>
      </Box>

      {/* Player */}
      <Paper
        elevation={0}
        sx={{
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '12px',
          bgcolor: 'background.paper',
          boxShadow: '0 6px 20px rgba(0, 51, 102, 0.07)',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            aspectRatio: '16 / 9',
            bgcolor: '#050505',
          }}
        >
          <iframe
            key={activeVideo.id}
            title={activeVideo.title}
            src={embedUrl}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              border: 0,
            }}
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            gap: 1.5,
            flexDirection: { xs: 'column', sm: 'row' },
            px: 2,
            py: 1.5,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{ color: 'primary.main', fontWeight: 800, letterSpacing: '0.06em', fontSize: '0.68rem', textTransform: 'uppercase' }}
            >
              Assistindo agora · Vídeo {activeIndex + 1}
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.25 }}>
              {activeVideo.title}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              justifyContent: { xs: 'space-between', sm: 'flex-end' },
              flexShrink: 0,
            }}
          >
            <IconButton
              onClick={handlePrevious}
              size="small"
              aria-label="Vídeo anterior"
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '8px',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: 'primary.main', color: 'primary.main', bgcolor: 'rgba(0, 51, 102, 0.06)' },
              }}
            >
              <ArrowBackIosNewIcon sx={{ fontSize: 15 }} />
            </IconButton>
            <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary', minWidth: 42, textAlign: 'center' }}>
              <Box component="span" sx={{ color: 'primary.main', fontWeight: 800 }}>{activeIndex + 1}</Box>
              {' / '}{tutorialVideos.length}
            </Typography>
            <IconButton
              onClick={handleNext}
              size="small"
              aria-label="Próximo vídeo"
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '8px',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: 'primary.main', color: 'primary.main', bgcolor: 'rgba(0, 51, 102, 0.06)' },
              }}
            >
              <ArrowForwardIosIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Box>
        </Box>
      </Paper>

      {/* Lista de vídeos da série */}
      <Box sx={{ mt: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 800, mb: 1, display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary', letterSpacing: '0.02em' }}>
          TODOS OS VÍDEOS DA SÉRIE
          <Chip size="small" label={tutorialVideos.length} sx={{ fontWeight: 800, height: 18, fontSize: '0.7rem' }} />
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, minmax(0, 1fr))',
              sm: 'repeat(3, minmax(0, 1fr))',
              lg: 'repeat(5, minmax(0, 1fr))',
            },
            gap: 1.25,
          }}
        >
          {tutorialVideos.map((video, index) => {
            const isActive = index === activeIndex;

            return (
              <Box
                key={video.id}
                component="button"
                type="button"
                onClick={() => setActiveIndex(index)}
                aria-pressed={isActive}
                sx={{
                  p: 0,
                  overflow: 'hidden',
                  textAlign: 'left',
                  cursor: 'pointer',
                  borderRadius: '10px',
                  border: '1px solid',
                  borderColor: isActive ? 'primary.main' : 'divider',
                  bgcolor: 'background.paper',
                  boxShadow: isActive ? '0 0 0 2px rgba(0, 51, 102, 0.18)' : 'none',
                  transition: 'border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
                  '&:hover': {
                    borderColor: 'primary.main',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 18px rgba(0, 51, 102, 0.14)',
                  },
                  '&:hover .thumb-img': { transform: 'scale(1.05)' },
                  '&:hover .thumb-play': { opacity: 1, transform: 'translate(-50%, -50%) scale(1)' },
                }}
              >
                <Box sx={{ position: 'relative', aspectRatio: '16 / 9', bgcolor: '#050505', overflow: 'hidden' }}>
                  <Box
                    component="img"
                    className="thumb-img"
                    src={`https://img.youtube.com/vi/${video.id}/hqdefault.jpg`}
                    alt={video.title}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                      transition: 'transform 0.35s ease',
                    }}
                  />
                  {/* gradiente para legibilidade */}
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      background: isActive
                        ? 'linear-gradient(180deg, rgba(0,51,102,0.06) 0%, rgba(0,51,102,0.24) 100%)'
                        : 'linear-gradient(180deg, rgba(0,0,0,0) 45%, rgba(0,0,0,0.22) 100%)',
                    }}
                  />

                  {/* número do vídeo */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 6,
                      left: 6,
                      minWidth: 18,
                      height: 18,
                      px: 0.6,
                      borderRadius: '6px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.66rem',
                      fontWeight: 800,
                      color: '#fff',
                      bgcolor: 'rgba(0, 0, 0, 0.62)',
                      backdropFilter: 'blur(2px)',
                    }}
                  >
                    {index + 1}
                  </Box>

                  {/* selo "assistindo" no ativo */}
                  {isActive && (
                    <Chip
                      size="small"
                      label="Assistindo"
                      color="primary"
                      sx={{
                        position: 'absolute',
                        top: 6,
                        right: 6,
                        height: 18,
                        fontSize: '0.62rem',
                        fontWeight: 800,
                        '& .MuiChip-label': { px: 0.75 },
                      }}
                    />
                  )}

                  {/* botão play (some até hover, sempre visível no ativo) */}
                  <Box
                    className="thumb-play"
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: isActive ? 'translate(-50%, -50%) scale(1)' : 'translate(-50%, -50%) scale(0.85)',
                      opacity: isActive ? 1 : 0,
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      bgcolor: isActive ? 'rgba(0, 51, 102, 0.95)' : 'rgba(0, 0, 0, 0.6)',
                      boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
                      transition: 'opacity 0.2s ease, transform 0.2s ease',
                    }}
                  >
                    <PlayArrowIcon sx={{ fontSize: 20, ml: '1px' }} />
                  </Box>
                </Box>
                <Box sx={{ px: 1, py: 0.875 }}>
                  <Typography
                    sx={{
                      fontSize: '0.78rem',
                      fontWeight: 700,
                      lineHeight: 1.3,
                      color: isActive ? 'primary.main' : 'text.primary',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      minHeight: '2.05em',
                    }}
                  >
                    {video.title}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
